import type { TreeNode } from 'primeng/api';
import type { DocMatrixTreeNode } from '../models/api';

export function mapDocMatrixTreeNodes(
    nodes: DocMatrixTreeNode[],
    depth: number,
    counts = new Map<string, number>(),
): TreeNode[] {
    return nodes.map((node) => {
        const treeNode: TreeNode = {
            label: node.displayed_name,
            data: {
                nodeType: node.items && node.items.length > 0
                    ? (depth === 0 ? 'group' : 'subgroup')
                    : 'item',
                disabled: node.disabled,
                required: node.required,
                uuid: node.uuid,
                name: node.name,
                context: node.context,
                count: node.uuid ? (counts.get(node.uuid) ?? 0) : 0,
                warning: node.required && !node.items?.length && (node.uuid ? (counts.get(node.uuid) ?? 0) === 0 : true),
            },
            expanded: !!node.items && node.items.length > 0,
        };

        if (node.items && node.items.length > 0) {
            treeNode.children = mapDocMatrixTreeNodes(node.items, depth + 1, counts);
        }

        return treeNode;
    });
}

export function filterTreeByVoucherId(nodes: DocMatrixTreeNode[], voucherId: number): DocMatrixTreeNode[] {
    return nodes.reduce<DocMatrixTreeNode[]>((acc, node) => {
        if (node.items && node.items.length > 0) {
            const items = filterTreeByVoucherId(node.items, voucherId);
            if (items.length > 0) acc.push({ ...node, items });
        } else if (node.context?.voucher_id === voucherId) {
            acc.push(node);
        }
        return acc;
    }, []);
}

export function applyCountsToTree(nodes: TreeNode[], counts: Map<string, number>): TreeNode[] {
    return nodes.map((node) => {
        const count = node.data?.uuid ? (counts.get(node.data.uuid) ?? 0) : 0;
        const warning = node.data?.required && !node.children?.length && count === 0;
        const updated = { ...node, data: { ...node.data, count, warning } };
        if (node.children?.length) {
            updated.children = applyCountsToTree(node.children, counts);
        }
        return updated;
    });
}

export function collectItemUuids(nodes: TreeNode[]): Set<string> {
    const uuids = new Set<string>();
    for (const node of nodes) {
        if (node.data?.uuid) uuids.add(node.data.uuid);
        if (node.children?.length) {
            for (const uuid of collectItemUuids(node.children)) uuids.add(uuid);
        }
    }
    return uuids;
}

export function collectRequiredItemUuids(nodes: TreeNode[]): Set<string> {
    const uuids = new Set<string>();
    for (const node of nodes) {
        if (node.children?.length) {
            for (const uuid of collectRequiredItemUuids(node.children)) uuids.add(uuid);
        } else if (node.data?.required && node.data?.uuid) {
            uuids.add(node.data.uuid);
        }
    }
    return uuids;
}

export function countRequiredItems(nodes: TreeNode[]): number {
    return nodes.reduce((sum, node) => {
        if (node.children?.length) return sum + countRequiredItems(node.children);
        return sum + (node.data?.required ? 1 : 0);
    }, 0);
}

export function filterRequiredOnly(nodes: TreeNode[]): TreeNode[] {
    return nodes.reduce<TreeNode[]>((acc, node) => {
        if (node.children && node.children.length > 0) {
            const filteredChildren = filterRequiredOnly(node.children);
            if (filteredChildren.length > 0) {
                acc.push({ ...node, children: filteredChildren, expanded: true });
            }
        } else if (node.data?.required) {
            acc.push(node);
        }
        return acc;
    }, []);
}

export function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
    const lowerQuery = query.toLowerCase();
    return nodes.reduce<TreeNode[]>((acc, node) => {
        const labelMatches = node.label?.toLowerCase().includes(lowerQuery);

        if (node.children && node.children.length > 0) {
            if (labelMatches) {
                acc.push({ ...node, expanded: true });
            } else {
                const filteredChildren = filterTree(node.children, query);
                if (filteredChildren.length > 0) {
                    acc.push({ ...node, children: filteredChildren, expanded: true });
                }
            }
        } else if (labelMatches) {
            acc.push(node);
        }
        return acc;
    }, []);
}

export function buildCountMap(links: { item_uuid: string }[]): Map<string, number> {
    const counts = new Map<string, number>();
    for (const link of links) {
        counts.set(link.item_uuid, (counts.get(link.item_uuid) ?? 0) + 1);
    }
    return counts;
}
